import {
  EOrderEventActorType,
  EOrderEventSource,
  EOrderEventType,
  EOrderHistoryCategory,
  type IOrderHistoryActor,
} from '@mahawi/eshop-common/src/types';

import { COLOR } from '../theme/tokens';

export const ORDER_HISTORY_CATEGORY_LABEL: Record<
  EOrderHistoryCategory,
  string
> = {
  [EOrderHistoryCategory.STATUS]: 'Status',
  [EOrderHistoryCategory.EMAIL]: 'Emails',
  [EOrderHistoryCategory.PAYMENT]: 'Payments and invoices',
  [EOrderHistoryCategory.SHIPPING]: 'Shipping',
  [EOrderHistoryCategory.CHANGE]: 'Changes',
  [EOrderHistoryCategory.ADMIN_ACTION]: 'Admin actions',
  [EOrderHistoryCategory.NOTE]: 'Notes',
  [EOrderHistoryCategory.WITHDRAWAL]: 'Withdrawals',
};

export const ORDER_HISTORY_CATEGORY_COLOR: Record<
  EOrderHistoryCategory,
  string
> = {
  [EOrderHistoryCategory.STATUS]: COLOR.primary,
  [EOrderHistoryCategory.EMAIL]: COLOR.cyan[6]!,
  [EOrderHistoryCategory.PAYMENT]: COLOR.success,
  [EOrderHistoryCategory.SHIPPING]: COLOR.geekblue[6]!,
  [EOrderHistoryCategory.CHANGE]: COLOR.orange[6]!,
  [EOrderHistoryCategory.ADMIN_ACTION]: COLOR.purple[6]!,
  [EOrderHistoryCategory.NOTE]: COLOR.gold[6]!,
  [EOrderHistoryCategory.WITHDRAWAL]: COLOR.error,
};

export const ORDER_EVENT_TYPE_LABEL: Record<EOrderEventType, string> = {
  [EOrderEventType.STATUS_CHANGED]: 'Status changed',
  [EOrderEventType.TAG_ADDED]: 'Tag added',
  [EOrderEventType.TAG_REMOVED]: 'Tag removed',
  [EOrderEventType.ADDRESS_CHANGED]: 'Address changed',
  [EOrderEventType.NOTE_ADDED]: 'Note',
  [EOrderEventType.INVOICE_REQUESTED]: 'Invoice generated',
  [EOrderEventType.FAKTUROID_CONTACT_SYNCED]: 'Fakturoid contact synced',
  [EOrderEventType.PACKET_CREATED]: 'Packet creation requested',
  [EOrderEventType.PACKET_ADDED_MANUALLY]: 'Packet added manually',
  [EOrderEventType.PACKET_STATUS_SYNC_REQUESTED]:
    'Packet status sync requested',
  [EOrderEventType.ATTACHMENT_VISIBILITY_CHANGED]:
    'Attachment visibility changed',
  [EOrderEventType.SHIPPED_EMAIL_SENT]: 'Shipped email sent',
  [EOrderEventType.SHIPPING_TIME_EMAIL_SENT]: 'Shipping time email sent',
  [EOrderEventType.REVIEW_REQUEST_SENT]: 'Review request sent',
  [EOrderEventType.VAT_ID_VERIFIED]: 'VAT ID verified',
  [EOrderEventType.WAREHOUSE_ORDER_PLACED]: 'Order sent to warehouses',
  [EOrderEventType.REVIEW_STATUS_CHANGED]: 'Review status changed',
  [EOrderEventType.WAREHOUSE_ORDER_PAIRED]: 'Paired with warehouse order',
  [EOrderEventType.WAREHOUSE_ORDER_UNPAIRED]: 'Unpaired from warehouse order',
};

export const ORDER_EVENT_SOURCE_LABEL: Record<EOrderEventSource, string> = {
  [EOrderEventSource.ADMIN]: 'admin',
  [EOrderEventSource.CUSTOMER_ACCOUNT]: 'customer account',
  [EOrderEventSource.PAYMENT_GATEWAY]: 'payment gateway',
  [EOrderEventSource.CARRIER]: 'carrier',
  [EOrderEventSource.WITHDRAWAL]: 'withdrawal',
  [EOrderEventSource.GIFT_REGISTRY]: 'gift registry',
  [EOrderEventSource.WAREHOUSE_SYNC]: 'warehouse sync',
};

const EMAIL_TYPE_PREFIX = 'EMAIL_TYPE_';

export const formatEmailTypeCode = (code: string): string => {
  const words: string = code
    .replace(EMAIL_TYPE_PREFIX, '')
    .toLowerCase()
    .replace(/_/g, ' ');

  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const describeOrderHistoryActor = (
  actor: IOrderHistoryActor,
): string => {
  const staffName: string = actor.username ?? actor.email ?? 'Staff';

  switch (actor.actorType) {
    case EOrderEventActorType.STAFF:
      return actor.source === EOrderEventSource.ADMIN
        ? staffName
        : `${staffName} · ${ORDER_EVENT_SOURCE_LABEL[actor.source]}`;
    case EOrderEventActorType.CUSTOMER:
      return 'Customer';
    case EOrderEventActorType.SYSTEM:
      return `System · ${ORDER_EVENT_SOURCE_LABEL[actor.source]}`;
  }
};
